import React, { Component } from 'react'
import PageHeaderWrapper from '../../../components/PageHeaderWrapper'
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Spin,
  Tag,
  Empty,
  Popconfirm,
  Icon,
  notification,
} from 'antd'
import moment from 'moment'
import Request, { API_URL } from '../../../request'
import MediaViewer from '../../../components/MediaViewer'
import styles from './styles.less'

const fullUrl = (u) => {
  if (!u) return ''
  if (u.indexOf('http') === 0) return u
  return API_URL + (u.charAt(0) === '/' ? '' : '/') + u
}

const parseMedia = (data) => {
  if (!data) return { photos: [], videos: [] }
  if (Array.isArray(data)) {
    const photos = []
    const videos = []
    data.forEach((m) => {
      const type = (m && m.type) || ''
      if (type === 'video') videos.push(m)
      else photos.push(m)
    })
    return { photos, videos }
  }
  return {
    photos: data.photos || data.images || [],
    videos: data.videos || [],
  }
}

// Read the optional ?orderId= from the URL (route components here are rendered
// without router props, so we read window.location directly).
const orderIdFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search || '')
    return params.get('orderId') || ''
  } catch (e) {
    return ''
  }
}

// Full page that lists soft-deleted production media and lets the user restore
// each item back onto the board. Built as its own page (not a modal) so a large
// number of images can wrap/scroll freely and the Restore confirm works.
class DeletedProductionMedia extends Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      items: [],
      orderId: orderIdFromUrl(),
      restoringId: null,
      previewIndex: -1,
    }
  }

  componentDidMount() {
    this.fetch()
  }

  fetch = () => {
    const { orderId } = this.state
    this.setState({ loading: true })
    Request.deletedProductionMedia({ orderId, _t: Date.now() }).then(
      ({ error, data }) => {
        if (!error) {
          const { photos, videos } = parseMedia(data)
          this.setState({ loading: false, items: [...photos, ...videos] })
        } else {
          this.setState({ loading: false, items: [] })
          notification.error({ message: 'Failed to load deleted images' })
        }
      }
    )
  }

  handleSearch = () => this.fetch()

  handleReset = () => this.setState({ orderId: '' }, this.fetch)

  restore = async (item) => {
    if (!item || !item._id) return
    this.setState({ restoringId: item._id })
    const res = await Request.restoreProductionMedia({ mediaId: item._id })
    if (res && !res.error) {
      notification.success({ message: 'Media restored' })
      this.setState((s) => ({
        items: s.items.filter((m) => String(m._id) !== String(item._id)),
        restoringId: null,
      }))
    } else {
      notification.error({ message: (res && res.message) || 'Failed to restore media' })
      this.setState({ restoringId: null })
    }
  }

  getMediaItems = () => this.state.items.filter((m) => m && m.url)

  openPreview = (item) => {
    const idx = this.getMediaItems().findIndex(
      (m) => String(m._id) === String(item._id)
    )
    this.setState({ previewIndex: idx >= 0 ? idx : 0 })
  }

  closePreview = () => this.setState({ previewIndex: -1 })

  renderCard = (item) => {
    const url = fullUrl(item.url)
    const isVideo = item.type === 'video'
    const restoring = this.state.restoringId === item._id
    return (
      <Col xs={12} sm={8} md={6} lg={4} key={item._id} style={{ marginBottom: 16 }}>
        <div className={styles.boardCard} style={{ cursor: 'default' }}>
          <div
            className={styles.boardCardMedia}
            title="Click to preview"
            style={{ height: 110 }}
            onClick={() => this.openPreview(item)}
          >
            {isVideo ? (
              <video src={url} muted preload="metadata" draggable={false} />
            ) : (
              <img src={url} alt="" loading="lazy" draggable={false} />
            )}
            <button
              type="button"
              className={styles.boardCardPreviewBtn}
              title="Preview"
              onClick={(e) => {
                e.stopPropagation()
                this.openPreview(item)
              }}
            >
              <Icon type="eye" />
            </button>
          </div>
          <div className={styles.boardCardMeta}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {item.orderId ? <Tag style={{ margin: 0 }}>Order #{item.orderId}</Tag> : null}
              {item.userName ? <Tag style={{ margin: 0 }}>{item.userName}</Tag> : null}
              {item.subCategory ? <Tag style={{ margin: 0 }}>{item.subCategory}</Tag> : null}
              {item.deletedAt ? (
                <Tag style={{ margin: 0 }} color="red">
                  Deleted {moment(item.deletedAt).format('DD-MM-YYYY')}
                </Tag>
              ) : null}
            </div>
            <Popconfirm
              title="Restore this media?"
              okText="Restore"
              cancelText="Cancel"
              onConfirm={() => this.restore(item)}
            >
              <Button size="small" type="primary" icon="rollback" loading={restoring} block>
                Restore
              </Button>
            </Popconfirm>
          </div>
        </div>
      </Col>
    )
  }

  render() {
    const { loading, orderId } = this.state
    const items = this.getMediaItems()
    return (
      <PageHeaderWrapper title="Deleted Production Images">
        <Card className={styles.filterBar} bodyStyle={{ paddingBottom: 0 }}>
          <Row gutter={12}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                style={{ marginBottom: 12 }}
                placeholder="Order ID (leave blank for all)"
                value={orderId}
                allowClear
                onChange={(e) => this.setState({ orderId: e.target.value })}
                onPressEnter={this.handleSearch}
              />
            </Col>
            <Col xs={24} sm={12} md={16} lg={18}>
              <Button
                type="primary"
                icon="search"
                onClick={this.handleSearch}
                style={{ marginRight: 8, marginBottom: 12 }}
              >
                Search
              </Button>
              <Button icon="reload" onClick={this.handleReset} style={{ marginRight: 8, marginBottom: 12 }}>
                Reset
              </Button>
              <Button icon="sync" loading={loading} onClick={this.fetch} style={{ marginBottom: 12 }}>
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>

        <Card bordered>
          <Spin spinning={loading}>
            <div style={{ marginBottom: 12, color: '#888' }}>
              {items.length
                ? `${items.length} deleted ${items.length === 1 ? 'image' : 'images'}${
                    orderId ? ` for Order #${orderId}` : ''
                  }`
                : null}
            </div>
            {items.length ? (
              <Row gutter={12}>{items.map((item) => this.renderCard(item))}</Row>
            ) : (
              !loading && (
                <Empty
                  description={
                    orderId
                      ? `No deleted images for Order #${orderId}`
                      : 'No deleted images'
                  }
                  style={{ padding: '40px 0' }}
                />
              )
            )}
          </Spin>
        </Card>

        <MediaViewer
          lightboxOnly
          items={items}
          openIndex={this.state.previewIndex}
          onClose={this.closePreview}
        />
      </PageHeaderWrapper>
    )
  }
}

export default DeletedProductionMedia
